'use client';

import { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Icon from '@/components/ui/AppIcon';

interface FishOption {
  id: string;
  name: string;
  color: string;
}

interface ComparisonToolProps {
  availableFish: FishOption[];
  comparisonData: any[];
}

const ComparisonTool = ({ availableFish, comparisonData }: ComparisonToolProps) => {
  const [selectedFish, setSelectedFish] = useState<string[]>([availableFish[0]?.id, availableFish[1]?.id]);

  const toggleFishSelection = (fishId: string) => {
    if (selectedFish.includes(fishId)) {
      if (selectedFish.length > 1) {
        setSelectedFish(selectedFish.filter(id => id !== fishId));
      }
    } else {
      if (selectedFish.length < 4) {
        setSelectedFish([...selectedFish, fishId]);
      }
    }
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-popover border border-border rounded-md shadow-elevation-md p-3">
          <p className="text-sm font-medium text-foreground mb-2">{payload[0].payload.date}</p>
          {payload.map((entry: any, index: number) => (
            <div key={index} className="flex items-center gap-2 mb-1">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }} />
              <span className="text-sm text-foreground">{entry.name}:</span>
              <span className="font-data font-bold text-primary">₹{entry.value.toFixed(2)}</span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-sm p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-md">
          <Icon name="ChartBarSquareIcon" size={20} className="text-primary" />
        </div>
        <div>
          <h2 className="font-heading font-bold text-xl text-foreground">Price Comparison</h2>
          <p className="text-sm text-muted-foreground">Compare prices across multiple fish types</p>
        </div>
      </div>

      <div className="mb-6">
        <p className="text-sm font-medium text-foreground mb-3">
          Select fish to compare (up to 4):
        </p>
        <div className="flex flex-wrap gap-2">
          {availableFish.map((fish) => (
            <button
              key={fish.id}
              onClick={() => toggleFishSelection(fish.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md border-2 transition-smooth ${
                selectedFish.includes(fish.id)
                  ? 'border-primary bg-primary/10 text-primary' :'border-border bg-muted text-foreground hover:bg-border'
              }`}
            >
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: fish.color }} />
              <span className="font-medium text-sm">{fish.name}</span>
              {selectedFish.includes(fish.id) && (
                <Icon name="CheckIcon" size={16} />
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="h-80 mb-6">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={comparisonData}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.1)" />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 12 }}
              stroke="rgba(0,0,0,0.5)"
            />
            <YAxis
              tick={{ fontSize: 12 }}
              stroke="rgba(0,0,0,0.5)"
              label={{ value: 'Price (₹/kg)', angle: -90, position: 'insideLeft' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            {availableFish
              .filter(fish => selectedFish.includes(fish.id))
              .map((fish) => (
                <Line
                  key={fish.id}
                  type="monotone"
                  dataKey={fish.id}
                  stroke={fish.color}
                  strokeWidth={2}
                  dot={{ fill: fish.color, r: 3 }}
                  name={fish.name}
                />
              ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {availableFish
          .filter(fish => selectedFish.includes(fish.id))
          .map((fish) => {
            const fishData = comparisonData.map(d => d[fish.id]).filter(Boolean);
            const avgPrice = fishData.reduce((sum, price) => sum + price, 0) / fishData.length;
            const maxPrice = Math.max(...fishData);
            const minPrice = Math.min(...fishData);

            return (
              <div key={fish.id} className="bg-muted rounded-md p-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: fish.color }} />
                  <h3 className="font-heading font-semibold text-base text-foreground">{fish.name}</h3>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Average</p>
                    <p className="font-data font-bold text-sm text-foreground">₹{avgPrice.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Highest</p>
                    <p className="font-data font-bold text-sm text-success">₹{maxPrice.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Lowest</p>
                    <p className="font-data font-bold text-sm text-error">₹{minPrice.toFixed(2)}</p>
                  </div>
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
};

export default ComparisonTool;